import type { LensCard, LensCardHeight, LensCardSize } from '~/types/leLens';

/** The grid is eight columns wide on desktop. */
export const GRID_COLUMNS = 8;

/** What each size asks for, before a row is balanced. */
const BASE_SPAN: Record<LensCardSize, number> = { small: 1, medium: 2, large: 4, full: 8 };

/**
 * How wide a card may grow when a row has room left over. A metric stretched
 * across a whole row is a number floating in white space, so the compact sizes
 * stop at a quarter.
 */
const MAX_SPAN: Record<LensCardSize, number> = { small: 4, medium: 4, large: 8, full: 8 };

export const DEFAULT_SIZE: LensCardSize = 'large';

/**
 * Cards without a saved size still need a deliberate visual grammar. Metrics
 * are KPI tiles wherever they originate (manual card, askRichard, or an older
 * Lens spec), so their fallback is compact rather than the generic large card.
 */
export function resolveLensCardPresentation(card: Pick<LensCard, 'type' | 'size'>): {
  size: LensCardSize;
  compact: boolean;
} {
  const size = card.size ?? (card.type === 'metric' ? 'small' : DEFAULT_SIZE);
  return { size, compact: size === 'small' || size === 'medium' };
}

/**
 * Packs cards into rows and shares each row's spare columns among the cards in
 * it, so a row of four metrics fills the width instead of leaving half the
 * dashboard empty. Order is preserved: this changes widths, never sequence.
 */
export function distributeSpans(sizes: Array<LensCardSize | undefined>): Array<number> {
  const spans = sizes.map((size) => BASE_SPAN[size ?? DEFAULT_SIZE]);
  const caps = sizes.map((size) => MAX_SPAN[size ?? DEFAULT_SIZE]);
  const result = [...spans];

  let index = 0;
  while (index < spans.length) {
    // Greedily fill one row.
    const row: Array<number> = [];
    let used = 0;
    while (index < spans.length && used + spans[index] <= GRID_COLUMNS) {
      used += spans[index];
      row.push(index);
      index += 1;
    }
    // A card wider than a whole row still occupies its own.
    if (!row.length) {
      result[index] = Math.min(spans[index], GRID_COLUMNS);
      index += 1;
      continue;
    }
    balance(row, result, caps);
  }

  return result;
}

/** Hands out the row's spare columns one at a time, widest-capped first. */
function balance(row: Array<number>, result: Array<number>, caps: Array<number>): void {
  let spare = GRID_COLUMNS - row.reduce((total, position) => total + result[position], 0);
  if (spare <= 0) {
    return;
  }

  let progressed = true;
  while (spare > 0 && progressed) {
    progressed = false;
    for (const position of row) {
      if (spare <= 0) {
        break;
      }
      if (result[position] >= caps[position]) {
        continue;
      }
      result[position] += 1;
      spare -= 1;
      progressed = true;
    }
  }
}

/**
 * Height is chosen separately from width: a wide chart is not necessarily a
 * tall one, and a table wants room for rows rather than columns.
 */
const HEIGHTS: Record<LensCardHeight, { card: number; body: number }> = {
  // `card - body` is the chrome budget: title, footer, and the padding and
  // gaps around them. 148 - 72 = 76px was sized for a one-line title and no
  // footer. A metric card's title now always reserves two real lines (39px)
  // and its footer is always shown, not just when a cross-filter or contract
  // note earns it; a short card genuinely needs closer to 96px of chrome to
  // fit both without the figure and the footer landing on top of each other.
  // `body` stays the same: only the chrome the figure sits inside grows.
  short: { card: 172, body: 72 },
  standard: { card: 240, body: 148 },
  tall: { card: 336, body: 244 },
  xtall: { card: 460, body: 368 },
};

/** What a size implies when no height was chosen. */
const IMPLIED: Record<LensCardSize, LensCardHeight> = {
  small: 'short',
  medium: 'standard',
  large: 'tall',
  full: 'tall',
};

export function cardHeight(size: LensCardSize | undefined, height: LensCardHeight | undefined) {
  return HEIGHTS[height ?? IMPLIED[size ?? DEFAULT_SIZE]];
}

/**
 * The zoom slider and its caption need room below the plot: a legend row and
 * axis labels above the block itself, then the slider and caption on top of
 * that, close to 90px of body in the worst case. Short's 72px body cannot
 * afford that without squeezing the plot to nothing, so zoom is only offered
 * from standard up, the shortest entry in HEIGHTS the reservation actually
 * clears with a plot still worth drawing above it.
 */
export const MIN_ZOOM_BODY_HEIGHT = HEIGHTS.standard.body;

export function canFitZoomControl(bodyHeight: number): boolean {
  return bodyHeight >= MIN_ZOOM_BODY_HEIGHT;
}
